interface OdsuKeydownManager {
    detachEventListeners?: VoidFunction;
}

interface OdsuComponentUtils {
    keydownManager?: () => OdsuKeydownManager | undefined;
}

interface OdsuElement extends HTMLElement {
    [property: string]: unknown;
    component?: OdsuComponentUtils;
}

const ODSU_ELEMENT_PREFIX = 'owc-';
const WINDOW_BLUR_HANDLER_PREFIX = 'windowBlurHandler-';

const isOdsuElement = (element: Element): element is OdsuElement => element.localName.startsWith(ODSU_ELEMENT_PREFIX);

export const cleanupDisconnectedOdsuElement = (element: Element) => {
    if (!isOdsuElement(element) || element.isConnected) {
        return;
    }

    // ODSU's Custom Elements build installs this cleanup on the component instance, while Stencil
    // invokes the disconnected callback captured from the prototype. Call the intended cleanup
    // through ComponentUtils until it is fixed by ODSU.
    element.component?.keydownManager?.()?.detachEventListeners?.();

    Object.keys(element)
        .filter((property) => property.startsWith(WINDOW_BLUR_HANDLER_PREFIX))
        .forEach((property) => {
            const listener = element[property];
            if (typeof listener === 'function') {
                globalThis.removeEventListener('blur', listener as EventListener, true);
            }
        });
};

export const cleanupDisconnectedOdsuTree = (root: Node) => {
    if (!(root instanceof Element) || root.isConnected) {
        return;
    }

    cleanupDisconnectedOdsuElement(root);
    root.querySelectorAll('*').forEach(cleanupDisconnectedOdsuElement);
};

export const observeDisconnectedOdsuElements = (root: Node): VoidFunction => {
    const pendingRoots = new Set<Node>();
    let cleanupTimeout: ReturnType<typeof globalThis.setTimeout> | undefined;

    const flushPendingRoots = () => {
        cleanupTimeout = undefined;
        pendingRoots.forEach(cleanupDisconnectedOdsuTree);
        pendingRoots.clear();
    };

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => mutation.removedNodes.forEach((node) => pendingRoots.add(node)));

        // Stencil can synchronously move a node while rendering. Crossing a task boundary prevents
        // those temporary removals from being treated as final disconnections.
        if (pendingRoots.size > 0 && cleanupTimeout === undefined) {
            cleanupTimeout = globalThis.setTimeout(flushPendingRoots, 0);
        }
    });

    observer.observe(root, { childList: true, subtree: true });

    return () => {
        observer.disconnect();
        pendingRoots.clear();
        if (cleanupTimeout !== undefined) {
            globalThis.clearTimeout(cleanupTimeout);
        }
    };
};
